// Tryb — studio state.
//
// The server holds the truth; this holds a working copy so typing stays
// instant. Edits apply locally at once and are flushed to the API on a short
// debounce, and every gate (advance, publish, upload, verify) returns fresh
// server state that replaces the copy wholesale.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BANKS as BANK_BY_IFSC } from "@shared/fields.js";
import { api, ApiError } from "./api.js";
import { StudioContext } from "./studioContext.js";

const EMPTY = { vals: {}, state: {}, documents: {}, venues: [], trybs: [], verifyStatus: "Unverified" };

const SAVE_DEBOUNCE_MS = 400;

export function StudioProvider({ children }) {
  const [studio, setStudio] = useState(EMPTY);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // Answers edited since the last flush, and the timer that will send them.
  const pending = useRef({});
  const timer = useRef(null);
  const toastTimer = useRef(null);

  useEffect(() => {
    api
      .studio()
      .then((s) => { setStudio(s); setReady(true); })
      .catch((e) => { setLoadError(e); setReady(true); });
    return () => { clearTimeout(timer.current); clearTimeout(toastTimer.current); };
  }, []);

  const showToast = useCallback((title, description) => {
    setToast({ title, description });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4600);
  }, []);

  const dismissToast = useCallback(() => {
    clearTimeout(toastTimer.current);
    setToast(null);
  }, []);

  const flush = useCallback(async () => {
    const patch = pending.current;
    pending.current = {};
    if (!Object.keys(patch).length) return;

    setSaving(true);
    try {
      await api.saveAnswers(patch);
    } catch (e) {
      // A rejected draft means the local copy is ahead of what the server will
      // accept. Re-read rather than leaving the two quietly disagreeing.
      const detail = e instanceof ApiError && e.errors ? Object.values(e.errors)[0] : e.message;
      showToast("That answer didn’t save", detail);
      api.studio().then(setStudio).catch(() => {});
    } finally {
      setSaving(false);
    }
  }, [showToast]);

  /** Set one field. Applies locally now, reaches the server shortly after. */
  const setField = useCallback(
    (fieldId, value) => {
      setStudio((s) => {
        const vals = { ...s.vals, [fieldId]: value };

        // Typing a valid IFSC fills in the bank name, as the design does.
        if (fieldId === "A5.08" && typeof value === "string" && /^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) {
          const bank = BANK_BY_IFSC[value.slice(0, 4)];
          if (bank) {
            vals["A5.09"] = bank;
            pending.current["A5.09"] = bank;
          }
        }
        return { ...s, vals };
      });

      pending.current[fieldId] = value;
      clearTimeout(timer.current);
      timer.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  /** Push any queued edits, then run an action that needs the server current. */
  const withFlush = useCallback(
    async (action) => {
      clearTimeout(timer.current);
      await flush();
      return action();
    },
    [flush],
  );

  const applyStudio = useCallback((next) => {
    if (next && next.vals) setStudio((s) => ({ ...s, ...next }));
  }, []);

  const refresh = useCallback(() => api.studio().then(setStudio), []);

  const value = useMemo(
    () => ({ ...studio, ready, loadError, saving, toast, showToast, dismissToast, setField, withFlush, applyStudio, refresh }),
    [studio, ready, loadError, saving, toast, showToast, dismissToast, setField, withFlush, applyStudio, refresh],
  );

  return <StudioContext.Provider value={value}>{children}</StudioContext.Provider>;
}
